// src/components/ui/Medallion.jsx
// STATUS: active | UPDATED: 2026-09-27
// LOG:
//   2026-09-27  Four glyphs for the manager marks.
//   2026-09-27  New. How a mark is drawn, everywhere it appears.
//
// A MARK LOOKS LIKE SOMETHING YOU WERE HANDED, NOT SOMETHING YOU UNLOCKED.
//
// Closer to a lapel pin or a challenge coin than to a sticker. A dark disc,
// a gold hairline inside the rim, the icon in the middle. No confetti, no
// glow, no bounce: a store guy handing somebody a coin does not do a dance.
//
// GOLD BELONGS TO MARKS AND NOTHING ELSE. The token has been in the theme since
// the contrast pass and nothing used it, which makes it the one colour in this
// product with no other meaning attached. Oxblood is structure, green is done,
// amber is attention. Gold is somebody noticed you. Keep it that way: the day a
// progress bar turns gold, a mark stops standing out.
//
// THE DISC DOES NOT INVERT, which is why it is `mark` and not `ink`. `ink` goes
// to paper on dark and light gold on paper is invisible. A coin is dark in any
// light. The two tokens live in the theme beside `gold`.
//
// THE ICONS ARE STAND-INS. docs/MARKS.md is the brief for a designer. When the
// real set exists it replaces the lucide icon inside this disc and nothing else
// changes, which is why every screen draws a mark through this file.

import { Box, Flex, Icon } from '@chakra-ui/react'
import {
  Award, Target, Hand, MessageCircleQuestion, Users, Lightbulb, Gauge, Wrench, Scale,
  PhoneIncoming, HeartHandshake, Sunrise, Eye, MessageSquare, Megaphone,
} from 'lucide-react'
import { markByKey } from '../../content/marks'

// Named, not `import * as`, because the star import pulls every icon lucide
// ships into the bundle. tests/marks.test.mjs fails if a mark names an icon
// that is not in this map.
const GLYPHS = {
  Target, Hand, MessageCircleQuestion, Users, Lightbulb, Gauge, Wrench, Scale,
  PhoneIncoming, HeartHandshake, Sunrise, Eye, MessageSquare, Megaphone,
}

export default function Medallion({ mark, size = 44, muted = false, ...rest }) {
  const m = typeof mark === 'string' ? markByKey(mark) : mark
  const Glyph = (m && GLYPHS[m.icon]) || Award
  const inset = Math.max(2, Math.round(size * 0.07))
  return (
    <Flex
      boxSize={`${size}px`}
      borderRadius="full"
      bg={muted ? 'surface' : 'mark'}
      color={muted ? 'inkDim' : 'onMark'}
      align="center"
      justify="center"
      position="relative"
      flexShrink={0}
      aria-hidden="true"
      {...rest}
    >
      {/* The hairline sits inside the rim, which is what makes a disc read as
          struck rather than printed. */}
      <Box
        position="absolute"
        inset={`${inset}px`}
        borderRadius="full"
        border="1px solid"
        borderColor={muted ? 'line' : 'onMark'}
        opacity={muted ? 1 : 0.55}
      />
      <Icon as={Glyph} boxSize={`${Math.round(size * 0.42)}px`} strokeWidth={1.75} />
    </Flex>
  )
}
